import { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { UseCarouselProps } from '../types/types';
import { CARD_WIDTH_VALUES } from '../constants';

export const useCarousel = <T,>({ gap = 16, items, useAnimation }: UseCarouselProps<T>) => {
  const theme = useTheme();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [dynamicGap, setDynamicGap] = useState<number | undefined>(undefined);
  const carouselRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getCardWidthPx = useCallback(
    (containerWidth: number): number => {
      const breakpoints = theme.breakpoints.values;

      if (containerWidth >= breakpoints.xl) {
        return CARD_WIDTH_VALUES.xl;
      } else if (containerWidth >= breakpoints.lg) {
        return CARD_WIDTH_VALUES.lg;
      } else if (containerWidth >= breakpoints.md) {
        return CARD_WIDTH_VALUES.md;
      } else if (containerWidth >= breakpoints.sm) {
        return CARD_WIDTH_VALUES.sm;
      } else {
        return CARD_WIDTH_VALUES.xs;
      }
    },
    [theme.breakpoints.values]
  );

  const calculateDynamicGap = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const cardWidthPx = getCardWidthPx(containerWidth);
    const totalItemsWidth = items.length * cardWidthPx;
    const gapNum = typeof gap === 'number' ? gap : parseFloat(gap as string) || 16;
    const totalGapSpace = (items.length - 1) * gapNum;
    const totalWidthWithDefaultGap = totalItemsWidth + totalGapSpace;

    if (totalWidthWithDefaultGap <= containerWidth && items.length > 0) {
      const remainingSpace = containerWidth - totalItemsWidth;
      const newGap = items.length > 1 ? remainingSpace / (items.length - 1) : 0;
      setDynamicGap(newGap);
    } else {
      setDynamicGap(undefined);
    }
  }, [items.length, gap, getCardWidthPx]);

  const updateScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    calculateDynamicGap();

    const resizeObserver = new ResizeObserver(() => {
      calculateDynamicGap();
      updateScrollButtons();
    });

    const container = scrollContainerRef.current;
    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateDynamicGap, updateScrollButtons]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollButtons();
    calculateDynamicGap();

    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', () => {
      updateScrollButtons();
      calculateDynamicGap();
    });

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons, items.length, calculateDynamicGap]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
        e.preventDefault();
        const scrollAmount = e.deltaX || e.deltaY;
        container.scrollBy({
          left: scrollAmount,
          behavior: 'auto',
        });
      }
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const currentGap = dynamicGap !== undefined ? dynamicGap / 8 : typeof gap === 'number' ? gap / 8 : parseFloat(gap as string) / 8 || 2;

  const handlePrevious = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const cardWidthPx = getCardWidthPx(containerWidth);
    const gapNum = typeof gap === 'number' ? gap : parseFloat(gap as string) || 16;
    const scrollAmount = cardWidthPx + gapNum;

    container.scrollBy({
      left: -scrollAmount,
      behavior: useAnimation ? 'smooth' : 'auto',
    });
  }, [gap, useAnimation, getCardWidthPx]);

  const handleNext = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const cardWidthPx = getCardWidthPx(containerWidth);
    const gapNum = typeof gap === 'number' ? gap : parseFloat(gap as string) || 16;
    const scrollAmount = cardWidthPx + gapNum;

    container.scrollBy({
      left: scrollAmount,
      behavior: useAnimation ? 'smooth' : 'auto',
    });
  }, [gap, useAnimation, getCardWidthPx]);

  return {
    handlePrevious,
    handleNext,
    currentGap,
    canScrollLeft,
    canScrollRight,
    dynamicGap,
    carouselRef,
    scrollContainerRef,
  };
};
